import { Button, Col, Row, message } from "antd";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import PaymentApi from "../../api/PaymentApi";
import { userState } from "../../state/atoms";
import PlansTable from "../PlansTable";
import "./UpgradePage.scss";
import { env } from "../../env/env";
import { Loader } from "lucide-react";

export default function UpgradePage() {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const user = useRecoilValue(userState);
  const [loading, setLoading] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams();
  const [success] = useSearchParams();
  const [canceled] = useSearchParams();
  const [session_id] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("success")) {
      messageApi.success('You have been upgraded successfully!');
    }

    if (searchParams.get("canceled")) {
      messageApi.error('You have canceled the upgrade.');
    }
  }, [])

  async function upgrade(planId: any) {
    setLoading(true);

    PaymentApi.createCheckoutSession(planId).then(resp => {
      setLoading(false);

      if (resp.data) {
        window.location.href = resp.data;
      } else {
        console.log(resp)
      }
    });
  }

  const [yearly, setYearly] = useState<boolean>(false)
  const [isAnnual, setIsAnnual] = useState<boolean>(true)

  return (
    <div>
      <Helmet>
        <title>Upgrade | Templated</title>
      </Helmet>

      <div className="flex justify-center max-w-[14rem] m-auto mb-8 lg:mb-8">
        <div className="relative flex w-full p-1 bg-white dark:bg-slate-900 rounded-full border">
          <span className="absolute inset-0 m-1 pointer-events-none" aria-hidden="true">
            <span className={`absolute inset-0 w-1/2 bg-primary rounded-full shadow-sm shadow-indigo-950/10 transform transition-transform duration-150 ease-in-out ${yearly ? 'translate-x-0' : 'translate-x-full'}`}></span>
          </span>
          <button
            className={`relative flex-1 text-sm font-medium h-8 rounded-full focus-visible:outline-none focus-visible:ring focus-visible:ring-indigo-300 dark:focus-visible:ring-slate-600 transition-colors duration-150 ease-in-out ${yearly ? 'text-white' : 'text-slate-500 dark:text-slate-400'}`}
            onClick={() => setYearly(true)}
            aria-pressed={isAnnual}>
            Yearly <span className={`${yearly ? 'text-indigo-200' : 'text-slate-400 dark:text-slate-500'}`}>-15%</span>
          </button>
          <button
            className={`relative flex-1 text-sm font-medium h-8 rounded-full focus-visible:outline-none focus-visible:ring focus-visible:ring-indigo-300 dark:focus-visible:ring-slate-600 transition-colors duration-150 ease-in-out ${yearly ? 'text-slate-500 dark:text-slate-400' : 'text-white'}`}
            onClick={() => setYearly(false)}
            aria-pressed={isAnnual}>
            Monthly
          </button>
        </div>
      </div>

      <div className="max-w-sm mx-auto grid gap-6 lg:grid-cols-3 items-start lg:max-w-6xl">
        <PricingTab
          yearly={yearly}
          planName="Starter"
          price={{ yearly: 299, monthly: 29 }}
          planDescription="Automate your images and PDFs"
          features={[
            '1,000 images or PDFs',
            'Up to 10 templates',
            'No Code integrations',
            'Email Support',
          ]}
          upgrade={upgrade}
          loading={loading}
          planId={env.plans.starter.id} />

        <PricingTab
          yearly={yearly}
          popular={true}
          planName="Scale"
          price={{ yearly: 999, monthly: 99 }}
          planDescription="Scale your automations"
          features={[
            '5,000 images or PDFs',
            'Up to 25 templates',
            'All Starter Plan benefits',
            'Email + Chat Support',
          ]}
          upgrade={upgrade}
          loading={loading}
          planId={env.plans.scale.id} />

        <PricingTab
          yearly={yearly}
          planName="Enterprise"
          price={{ yearly: 1799, monthly: 179 }}
          planDescription="Grow the machine"
          features={[
            '25,000 images or PDFs',
            'Unlimited templates',
            'All Scale Plan benefits',
            'Priority Support',
          ]}
          upgrade={upgrade}
          loading={loading}
          planId={env.plans.enterprise.id} />

      </div>

    </div>
  )
}

interface PricingTabProps {
  yearly: boolean;
  popular?: boolean;
  planName: string;
  price: {
    monthly: number;
    yearly: number;
  }
  planDescription: string;
  features: string[];
  upgrade?: any;
  planId: string;
  loading: boolean;
}

function PricingTab(props: PricingTabProps) {
  return (
    <div className={`h-full ${props.popular ? '' : 'dark'} dark`}>
      <div className={`relative flex flex-col h-full p-6 rounded-xl bg-white border border-slate-200 shadow-lg shadow-slate-950/5 ${props.popular ? '!border-accent border-2 ' : ''}`}>
        {props.popular && (
          <div className="absolute top-0 right-0 mr-6 -mt-4">
            <div className="inline-flex items-center text-xs font-semibold py-1.5 px-3 bg-accent text-white rounded-full shadow-sm shadow-slate-950/5">Popular</div>
          </div>
        )}
        <div className="mb-5">
          <div className="text-slate-500 font-semibold mb-1 text-3xl tracking-tighter">{props.planName}</div>
          <div className="inline-flex items-baseline mb-2">
            <span className="text-slate-900 font-bold text-3xl">$</span>
            <span className="text-slate-900 font-bold text-5xl">{props.yearly ? props.price.yearly : props.price.monthly}</span>
            <span className="text-slate-500 font-medium text-2xl">{props.yearly ? '/year' : '/mo'}</span>
          </div>
          <div className="text-lg text-slate-500 mb-5">{props.planDescription}</div>
          <Button type="primary" size="large" loading={props.loading} className="w-full transition-colors duration-150 !text-xl !py-2 !h-auto" 
          onClick={() => props.upgrade(props.planId)}>
            Upgrade
          </Button>
          {/* <button className="w-full inline-flex justify-center whitespace-nowrap rounded-md bg-primary 
          px-3.5 py-2.5 text-xl font-medium text-white shadow-sm shadow-indigo-950/10 hover:bg-primary 
          focus-visible:outline-none focus-visible:ring focus-visible:ring-indigo-300 dark:focus-visible:ring-slate-600 
          transition-colors duration-150" 
          onClick={() => props.upgrade(props.planId)}>
            Upgrade
          </button> */}
        </div>
        <div className="text-slate-900 dark:text-slate-200 font-medium mb-3">Includes:</div>
        <ul className="text-slate-600 dark:text-slate-400 text-sm space-y-3 grow">
          {props.features.map((feature, index) => {
            return (
              <li key={index} className="flex items-center text-xl">
                <svg className="w-3 h-3 fill-emerald-500 mr-3 shrink-0" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                </svg>
                <span>{feature}</span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
